import request from '../../utils/request';

// OVO-101 — partner-scoped Sites (Address Book) API.

export function listSitesForPartner(partnerId, q) {
  const qs = q ? `?q=${encodeURIComponent(q)}` : '';
  return request(`/partners/${partnerId}/sites${qs}`, { method: 'GET' });
}

// Partner-viewer read-only listing — scoped to the caller's own partner
// via the JWT (no partnerId in the URL; the backend derives it).
export function getMySites() {
  return request(`/partners/me/sites`, { method: 'GET' });
}

export function createSite(partnerId, data) {
  return request(`/partners/${partnerId}/sites`, { method: 'POST', data });
}

export function updateSite(id, data) {
  return request(`/sites/${id}`, { method: 'PATCH', data });
}

export function deleteSite(id) {
  return request(`/sites/${id}`, { method: 'DELETE' });
}

// OVO-169 — passage history for a Site (orders that visited it: date, driver,
// status, weight). Backs the Sites map view's detail panel.
export function getSitePassages(id) {
  return request(`/sites/${id}/passages`, { method: 'GET' });
}

// OVO-169 — partner-viewer variant: passage history scoped to the caller's own
// partner (404 on another partner's site — OVO-002 isolation).
export function getMySitePassages(id) {
  return request(`/partners/me/sites/${id}/passages`, { method: 'GET' });
}

// Discover: scan the partner's orders and propose candidate Sites the
// catalog doesn't yet cover.
export function discoverSites(partnerId) {
  return request(`/partners/${partnerId}/sites/discover`, { method: 'GET' });
}

// Bulk-create — used by the Discover "Add selected" flow. Sends a list
// of CreateSiteDto-shaped rows; the backend silently skips any that
// collide with existing Sites (operator may have unchecked or edited
// the dups in the modal already; race-safe defense in depth).
export function bulkCreateSites(partnerId, sites) {
  return request(`/partners/${partnerId}/sites/bulk`, {
    method: 'POST',
    data: { sites },
  });
}

// Merge — consolidate near-duplicates. `primaryId` survives, every
// order pointing at any of `mergeIds` gets reassigned to the primary,
// then the merge IDs are deleted. Optional name/address overrides
// canonicalize the primary's identity inline.
export function mergeSites(partnerId, payload) {
  return request(`/partners/${partnerId}/sites/merge`, {
    method: 'POST',
    data: payload,
  });
}

// OVO-142 — scan the existing catalog for near-duplicate Sites and return
// proposed merge clusters (each with a suggested canonical). Read-only;
// the operator confirms each cluster, which then calls `mergeSites`.
export function getMergeSuggestions(partnerId) {
  return request(`/partners/${partnerId}/sites/merge-suggestions`, { method: 'GET' });
}

// Bulk-delete — drop N Sites in one go. Uses POST not DELETE because
// DELETE with a body is non-standard. Orders that referenced the
// deleted Sites get their FK set to null (snapshot stays).
export function bulkDeleteSites(partnerId, ids) {
  return request(`/partners/${partnerId}/sites/bulk-delete`, {
    method: 'POST',
    data: { ids },
  });
}

// OVO-148 — re-run the retroactive backfill for every active Site of the
// partner. Links historical orders whose address format drifted (trailing
// space / empty comma segment) — Discover-schedules only sees Site-linked
// orders, so unlinked history = empty discovery.
export function backfillPartnerSites(partnerId) {
  return request(`/partners/${partnerId}/sites/backfill`, { method: 'POST' });
}

// OVO-103 Phase 2 — per-Site hint photos. Auto-attached to orders
// that link to the Site (PICKUP_HINT / DELIVERY_HINT FileOrder rows
// on the order; the driver-app picks them up via existing plumbing).
export function getSite(id) {
  return request(`/sites/${id}`, { method: 'GET' });
}

export function listSiteFiles(id) {
  return request(`/sites/${id}/files`, { method: 'GET' });
}

export function addSiteFile(id, fileId, type) {
  return request(`/sites/${id}/files`, {
    method: 'POST',
    data: { fileId, type },
  });
}

export function removeSiteFile(siteFileId) {
  return request(`/site-files/${siteFileId}`, { method: 'DELETE' });
}
